import { Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { toDays } from "@smartrr/shared/utils/renderViewDate";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback } from "react";

import { IModal } from "@customer-app/account/views/OperationAquarius/GlobalModal";
import { usePurchaseablesForAdding } from "@customer-app/account/views/OperationAquarius/hooks/usePurchaseablesForAdding";
import { useMobileQuery, useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
import { useToggleModal } from "@customer-app/state/actionCreators/modal";

import { ActionButtons } from "./ActionButtons";
import { CardProductImageBackground } from "./CardProductImageBackground";
import { DisplayLineItemsRow } from "./DisplayLineItemsRow";
import { SubscriptionManagementCardBadges } from "./SubscriptionManagementCardBadges";
import { useBasicSubscriptionInfo } from "../../../../Modals/ManageSubscriptionsModal/hooks/useBasicSubscriptionInfo";
import { useFrequencyString } from "../../../../utils";
import { ActionButtonModals, IBadgeSubscriptionStatus, ISubscriptionManagementCard } from "../../types";
import { getDeliveryForCPS, useButtonCopy } from "../../utils";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

export const SubscriptionManagementCard = ({
  purchaseState,
  showAllSubscriptions,
}: ISubscriptionManagementCard) => {
  const organization = useOrganization();
  const delivery = getDeliveryForCPS(purchaseState, organization)!;

  const {
    isPaused,
    isPrepaid,
    isDelayDateAvailable,
    isSellingPlanDeleted,
    gift,
    isPurchaseStatesInitialized,
    isSellingPlanGroupsLoading,
    skdIdx,
    bundle,
    containsOOSItems,
  } = useBasicSubscriptionInfo(delivery as PurchaseStateAndDelivery);
  const { schedule } = purchaseState;

  const isError = isSellingPlanDeleted && isPurchaseStatesInitialized && !isSellingPlanGroupsLoading;

  const isMobile = useMobileQuery();

  const toggleModal = useToggleModal();

  const purchaseablesForAdding = usePurchaseablesForAdding(delivery);

  const buttonCopy = useButtonCopy(delivery);

  const onSelectModalView = useCallback(
    <M extends ActionButtonModals>(modalType: ActionButtonModals = "SELECT_MODAL", props?: Partial<IModal[M]>) =>
      toggleModal(modalType, {
        deliveryId: getDeliveryId(delivery),
        delayDateAvailable: isDelayDateAvailable,
        gift: gift!,
        isSellingPlanDeleted,
        organization,
        purchasablesForAdding: purchaseablesForAdding,
        generatePurchasables: true,
        ...props,
      }),
    [
      delivery,
      gift,
      isDelayDateAvailable,
      isSellingPlanDeleted,
      organization,

      purchaseablesForAdding,
      toggleModal,
    ]
  );
  // For displaying the order frequency (ie 'Every x Months')
  const frequencyString = useFrequencyString(schedule);

  if (!delivery) {
    return <React.Fragment />;
  }

  const subscriptionStatus: IBadgeSubscriptionStatus = {
    isPrepaid,
    isError,
    isGifted: !!gift,
    containsOOSItems,
    bundle: !!bundle,
  };

  return (
    <AquariusCard className={prefixClassName("next-order-card")} sx={{ height: "100%" }} contained={false}>
      <CardProductImageBackground delivery={delivery} />
      <Stack direction="column" padding="20px" marginTop="-42px">
        <DisplayLineItemsRow purchaseState={purchaseState} skdIdx={skdIdx} hideAddons={showAllSubscriptions} />
        <Stack direction="column" gap="15px" marginTop="5px">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ marginBottom: "15px" }}
            spacing="7px"
          >
            <Typography variant="h4" margin="0" whiteSpace="nowrap">
              {showAllSubscriptions
                ? typedLocalize("subscriptions.yourSubscriptions.every", {
                    deliveryFrequency: toDays(frequencyString),
                  })
                : typedLocalize("subscriptions.yourNextOrder.title")}
            </Typography>
            <SubscriptionManagementCardBadges
              showAllSubscriptions={showAllSubscriptions}
              subscriptionStatus={subscriptionStatus}
              purchaseState={purchaseState}
              skdIdx={skdIdx}
            />
          </Stack>
        </Stack>
        <Stack direction={isMobile ? "column" : "row"} justifyContent={isMobile ? "start" : "space-between"}>
          <Typography variant="h3" minWidth={isMobile ? "auto" : "236px"} minHeight={"64px"}>
            {isPaused
              ? typedLocalize("badgeStatus.paused")
              : typedLocalize("utils.dates.toViewDateLongDay", {
                  date: delivery?.date,
                })}
          </Typography>
          <ActionButtons isPaused={isPaused} delivery={delivery} onSelectModalView={onSelectModalView} />
        </Stack>
        <AquariusButton
          filled
          onClick={() =>
            isPaused
              ? onSelectModalView("MANAGE_SUBSCRIPTIONS", {
                  initialView: "Resume",
                  deliveryId: getDeliveryId(delivery),
                  generatePurchasables: false,
                  purchasablesForAdding: [],
                })
              : onSelectModalView("SELECT_MODAL")
          }
        >
          {buttonCopy}
        </AquariusButton>
      </Stack>
    </AquariusCard>
  );
};
